import { useEffect, useMemo, useRef, useState, type FC } from 'react';

import { getDrrView, type DrrViewResponse, type DrrViewRow } from '@api/inventory';
import type { ReportScheduleFilters } from '@api/reportSchedules';
import type { DataSheetCaptureHandle } from '@components/ui/DataSheet';
import { compareDrrBySortBy } from '@utils/drrSort';

import {
    addScaledDrrRow,
    cloneDrrRow,
    getDrrRowId,
    makeEmptyDrrRow,
    recomputeDrrRow,
} from '../components/containers/inventoryExplosion';
import { DrrViewTable, rollupDrrRows } from '../components/containers/inventory';
import { useSkuExplosion } from '../hooks/useSkuExplosion';

/**
 * The headless email-render route: puppeteer (the report worker) opens this page with a
 * base64url `payload` query param, the page authenticates with the short-lived render
 * token inside it, fetches the SAME data the live view fetches, applies the stored view
 * state (filters, break-down/explosion, grouping, sub-group, sort, expansion, hidden
 * columns) through the grid's OWN hooks and components, and publishes one of two outputs:
 *
 *   output 'image' (default):
 *     <img id="email-render-result" src="data:image/jpeg..."> — the body screenshot
 *   output 'table':
 *     <script id="email-render-data" type="application/json">…</script> — the grid's
 *     rendered cells (text + colors) serialized, from which the worker builds the XLSX
 *     attachment. Because both outputs come from the same render, an attachment can never
 *     disagree with the screen.
 *
 * Completion is signaled with document.body.dataset.emailRenderDone / emailRenderError.
 * Currently supports DOH_BY_CHANNEL; other report types report an explicit error rather
 * than a silently wrong output.
 */

type RenderPayload = {
    token: string;
    type?: string;
    output?: 'image' | 'table';
    filters?: ReportScheduleFilters;
    rowLimit?: number;
    grouping?: {
        groupBy: string;
        subGroupBy?: string;
        sortBy?: string;
        expanded?: 'ALL' | 'NONE';
        expandedKeys?: string[];
    };
};

function parsePayload(): RenderPayload | null {
    try {
        const raw = new URLSearchParams(window.location.search).get('payload');
        if (!raw) return null;
        return JSON.parse(atob(raw.replaceAll('-', '+').replaceAll('_', '/')));
    } catch {
        return null;
    }
}

const GROUP_RESOLVERS: Record<string, (row: DrrViewRow) => string> = {
    category: (row) => row.category ?? 'Uncategorized',
    productGroup: (row) => row.product_group ?? 'Ungrouped',
    product_group: (row) => row.product_group ?? 'Ungrouped',
    tier: (row) => row.tier ?? 'No tier',
    isActive: (row) => (row.is_active ? 'Active' : 'Inactive'),
};

const fail = (message: string) => {
    document.body.dataset.emailRenderError = message;
};

const done = () => {
    document.body.dataset.emailRenderDone = '1';
};

const EmailRenderTable: FC = () => {
    const [payload] = useState<RenderPayload | null>(() => {
        const parsed = parsePayload();
        // The token must be in place BEFORE any API call renders — the shared API client
        // reads it from localStorage on every request.
        if (parsed?.token) {
            try {
                window.localStorage.setItem('token', parsed.token);
            } catch {
                // fall through — the fetch will fail and report below
            }
        }
        return parsed;
    });
    const [resp, setResp] = useState<DrrViewResponse | null>(null);
    const [baseRows, setBaseRows] = useState<DrrViewRow[]>([]);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [tableJson, setTableJson] = useState<string | null>(null);
    const captureRef = useRef<DataSheetCaptureHandle | null>(null);

    useEffect(() => {
        if (!payload?.token) {
            fail('Missing or malformed render payload');
            return;
        }
        if (payload.type !== 'DOH_BY_CHANNEL') {
            fail(`Report type ${payload.type ?? '(none)'} has no headless render yet`);
            return;
        }
        const filters = payload.filters ?? {};
        getDrrView({
            categories: filters.categories,
            productGroups: filters.productGroups,
            tiers: filters.tiers,
            isActive: filters.isActive,
            search: filters.search,
            drrWindow: filters.drrWindow,
            channelKey: filters.channelKey,
            page: 1,
            pageSize: 100_000,
        })
            .then((response) => {
                setBaseRows(response.data ?? []);
                setResp(response);
            })
            .catch((error: unknown) => {
                fail(error instanceof Error ? error.message : String(error));
            });
    }, [payload]);

    // The grid's OWN break-down: same hook, same per-view fold functions the live page
    // uses — when the stored filters say the view was exploded, the render is too.
    const { rows: explodedRows } = useSkuExplosion<DrrViewRow>({
        rows: baseRows,
        enabled: !!payload?.filters?.exploded,
        onEnabledChange: () => undefined,
        getId: getDrrRowId,
        cloneRow: cloneDrrRow,
        addScaled: addScaledDrrRow,
        recompute: recomputeDrrRow,
        makeEmptyRow: makeEmptyDrrRow,
    });

    const rows = useMemo(() => {
        const grouping = payload?.grouping;
        const resolver = grouping ? GROUP_RESOLVERS[grouping.groupBy] : undefined;
        if (!resolver) return explodedRows;
        const subResolver = grouping?.subGroupBy
            ? GROUP_RESOLVERS[grouping.subGroupBy]
            : undefined;
        return rollupDrrRows(
            explodedRows,
            resolver,
            subResolver,
            grouping?.sortBy
                ? compareDrrBySortBy(
                      grouping.sortBy as Parameters<typeof compareDrrBySortBy>[0],
                  )
                : undefined,
        );
    }, [explodedRows, payload]);

    // Data rendered → run the same capture pipeline the composer uses, then publish.
    // The once-ref guards React StrictMode's double effect invocation in dev — a second
    // capture while the first is in flight rejects with "already in progress".
    const captureStartedRef = useRef(false);
    useEffect(() => {
        if (!resp || !payload || captureStartedRef.current) return;
        captureStartedRef.current = true;
        const frame = requestAnimationFrame(() => {
            const handle = captureRef.current;
            if (!handle) {
                fail('Grid capture handle not mounted');
                return;
            }
            const options = {
                maxVisualRows: payload.rowLimit ?? 30,
                // Replay the expansion EXACTLY as captured: all open, all closed, or
                // the specific groups the operator had opened.
                expansion: payload.grouping
                    ? (payload.grouping.expandedKeys ??
                      payload.grouping.expanded ??
                      'ALL')
                    : undefined,
            };
            if (payload.output === 'table') {
                handle
                    // The attachment is the WHOLE filtered set, like the grid's own
                    // download — rowLimit applies to the body image only.
                    .captureViewData({ ...options, maxVisualRows: 1_000_000 })
                    .then((data) => {
                        // `</` would terminate the script element early; escape it so the
                        // JSON survives embedding.
                        setTableJson(JSON.stringify(data).replaceAll('</', '<\\/'));
                        requestAnimationFrame(done);
                    })
                    .catch((error: unknown) => {
                        fail(error instanceof Error ? error.message : String(error));
                    });
                return;
            }
            handle
                .captureViewImage(options)
                .then((result) => {
                    setImageSrc(result.dataUrl);
                    // Set AFTER the img src exists in state; the img renders next paint,
                    // and the worker reads the src only once this flag appears.
                    requestAnimationFrame(done);
                })
                .catch((error: unknown) => {
                    fail(error instanceof Error ? error.message : String(error));
                });
        });
        return () => cancelAnimationFrame(frame);
    }, [resp, payload, rows]);

    const config = (resp as unknown as {
        inventory_config?: { under_stock_limit?: number; over_stock_limit?: number };
    } | null)?.inventory_config;

    return (
        <div style={{ padding: 16, background: '#ffffff' }}>
            {tableJson && (
                <script
                    id="email-render-data"
                    type="application/json"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: tableJson }}
                />
            )}
            {imageSrc ? (
                <img
                    id="email-render-result"
                    src={imageSrc}
                    alt="Rendered table"
                    style={{ maxWidth: '100%' }}
                />
            ) : resp ? (
                <DrrViewTable
                    resp={resp}
                    rows={rows}
                    rollupLabel={payload?.grouping ? 'Group' : null}
                    // The live page derives this from its channel selector; without it the
                    // headless render labels the overall band "Channel Total" on an
                    // INTERNAL-pivot capture (and re-adds the Internal group the pivot hides).
                    isInternalPivot={payload?.filters?.channelKey === 'INTERNAL'}
                    underStockLimit={config?.under_stock_limit ?? 15}
                    overStockLimit={config?.over_stock_limit ?? 60}
                    inventoryPeriod="DAY"
                    hiddenColumns={payload?.filters?.hiddenColumns ?? []}
                    onChannelClick={() => undefined}
                    captureRef={captureRef}
                />
            ) : (
                <span>Rendering…</span>
            )}
        </div>
    );
};

export default EmailRenderTable;
